"use client";

import CustomImage from '@/components/image';
import styles from './_version-1.module.scss';
import Button from '@/components/button';
import PageAnchor from '@/components/page-anchor';
import HcpDrQuote from '@/components-hcp/dr-quote';

export default function HcpClinicalInformationDoseReductionsVersion1({ modalRef = null }) {

  return (
    <>
      <HcpDrQuote
        doctor={{
          name: 'Dr. Heidi Shea',
          specialty: 'Adult & Pediatric Endocrinologist',
          image: '/images/components/CAH-HCP-Specialist-Dr-Shea.png',
          note: 'Compensated by Neurocrine for sharing her views.'
        }}
        theme='tan'
      >
        I can only anticipate that being able to lower GC dose may be helpful in avoiding side effects of supraphysiologic dosing.”
      </HcpDrQuote>
      <PageAnchor anchorId="trial-1-efficacy-dose-reductions" />
      <section className={styles.container}>
        <div className="container container-medium">
          <p className="h1 page-heading w-full">GC dose reductions with CRENESSITY were durable through 2 years<sup>1,6,9</sup></p>
            <div className="flex justify-center">
              <div className="relative">
                <p className="h4 mb-6 w-full text-hcp-eggplant">
                  <strong>Change in daily GC dose (mean percent change from baseline)<sup>1,6,9</sup><span className="!font-normal">*</span><sup>†</sup></strong>
                </p>
                <div className={styles.ver1__chart}>
                  <picture>
                    <source
                      media="(max-width: 767px)"
                      srcSet="/images/page/hcp/clinical-information/Clinical-Information_Pediatric-GC-Doses_mobile.svg"
                      width={327}
                      height={1610}
                    />
                    <CustomImage
                      src="/images/page/hcp/clinical-information/Clinical-Information_Pediatric-GC-Doses.svg"
                      alt="GC doses were reduced by 18% with CRENESSITY vs a 6% increase with placebo at 28 weeks, an ~4x greater improvement."
                      width={779}
                      height={446}
                      className="mx-auto"
                    />
                  </picture>
                  <div className={styles.ver1__chart_callout}>
                    <div>GC dose reductions were maintained in children treated with CRENESSITY <strong>through</strong> <span className="text-nowrap"><strong>2 years</strong>.<sup className="!font-normal">1,6,9‡</sup></span></div>
                    <p>
                      Mean GC dose reduction at 2 years was <span className="text-nowrap mobile:text-wrap">~17% for</span> children who continued on CRENESSITY (n=84).<sup className="!font-normal">6‡</sup>
                      <span className={styles.ver1__chart_disclaimer}>These prespecified exploratory endpoints were not adjusted for multiplicity; these descriptive results could represent chance findings.<sup className="!font-normal">11</sup></span>
                    </p>
                  </div>
                  <div className={styles.ver1__subgroup}>
                    Consistent with the overall GC reduction endpoint, these subgroups favored CRENESSITY over placebo with greater dose reduction at week 28<sup className="!font-normal">22</sup>:
                    <ul>
                      <li>Age (&lt;12 years and 12-17 years)</li>
                      <li>Weight (&lt;55 kg and &ge;55 kg)</li>
                      <li>Baseline androstenedione levels (&le;ULN and &gt;ULN)</li>
                      <li>Baseline GC dose (&lt;16 mg/m<sup>2</sup>/day and &ge;16 mg/m<sup>2</sup>/day)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

          <ul className="footnotes">
            <li>
              <span className="hang">*</span>
              <span>
                Mean baseline GC doses in hydrocortisone equivalents were 16.4 ± 3.9 mg/m<sup>2</sup>/day for all participants, 16.3 ± 3.4 mg/m<sup>2</sup>/day for placebo, and 16.5 ± 4.2 mg/m<sup>2</sup>/day for CRENESSITY.<sup>9</sup>
              </span>
            </li>
            <li>
              <sup className="hang">&dagger;</sup>
              <span>
                Endpoints are presented as LSM change from baseline at week 28 and as mean change at 1 year.<sup>6</sup>
              </span>
            </li>
            <li>
              <sup className="hang">&Dagger;</sup>
              <span>
                The 2 year data for GC reduction were determined without protocol-defined reference to androstenedione level at the visit.<sup>6</sup>
              </span>
            </li>
          </ul>

          <div className={styles.buttonWrapper}>
            <Button
              type="button"
              variant={['outline--eggplant', 'hcp-big']}
              onClick={() => modalRef?.current?.open()}
              data-gtm-event-category="Action"
              data-gtm-event-action="Click"
              data-gtm-event-label="View high-dose GC effects"
            >View high-dose GC effects</Button>
          </div>
        </div>
      </section>
    </>
  );
}